import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { getFocusedRouteNameFromRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIconType } from '@thermenresorts/icons';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import AccountStackNavigator from 'src/presentation/navigation/navigators/AccountStackNavigator';
import BathCardStackNavigator from 'src/presentation/navigation/navigators/BathCardStackNavigator';
import BookingStackNavigator from 'src/presentation/navigation/navigators/BookingStackNavigator';
import HomeStackNavigator from 'src/presentation/navigation/navigators/HomeStackNavigator';
import InspirationStackNavigator from 'src/presentation/navigation/navigators/InspirationStackNavigator';
import LoyaltyStackNavigator from 'src/presentation/navigation/navigators/LoyaltyStackNavigator';
import { hiddenTabBarStyle, tabBarStyleWithInset } from 'src/presentation/navigation/styles/TabBarStyles';
import type { BottomTabNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import { useActiveLanguage } from 'src/presentation/hooks/useActiveLanguage';

const Tab = createBottomTabNavigator<BottomTabNavigation>();

type TabBarRoute = Parameters<typeof getFocusedRouteNameFromRoute>[0];

const routesWithoutTabBar: string[] = [Constants.screenLoyaltyResort];

const hidesTabBar = (route: TabBarRoute): boolean => {
	const routeName = getFocusedRouteNameFromRoute(route);

	return routeName !== undefined && routesWithoutTabBar.includes(routeName);
};

export default function BottomTabNavigator() {
	useActiveLanguage();

	const insets = useSafeAreaInsets();
	const computedTabBarStyle = tabBarStyleWithInset(insets.bottom);

	return (
		<Tab.Navigator
			screenOptions={{
				headerShown: false,
				tabBarActiveTintColor: Colors.green,
				tabBarInactiveTintColor: Colors.gray500,
				tabBarLabelPosition: 'below-icon',
				tabBarLabelStyle: {
					fontSize: Dimens.fontSize11,
				},
				tabBarStyle: computedTabBarStyle,
			}}
		>
			<Tab.Screen
				component={HomeStackNavigator}
				name={Constants.screenTabHome}
				options={{
					title: Translations.tabsHome,
					tabBarItemStyle: { flex: Dimens.flex08 },
					tabBarIcon: ({ color }) => <SvgIcon name={SvgIconType.homeAlt} size={Dimens.iconSize16} tintColor={color} />,
				}}
			/>
			<Tab.Screen
				component={BookingStackNavigator}
				name={Constants.screenTabBooking}
				options={{
					title: Translations.tabsBooking,
					tabBarIcon: ({ color }) => (
						<SvgIcon name={SvgIconType.calendarRounded} size={Dimens.iconSize16} tintColor={color} />
					),
				}}
			/>
			<Tab.Screen
				component={LoyaltyStackNavigator}
				name={Constants.screenTabLoyalty}
				options={({ route }) => ({
					headerShown: false,
					title: Translations.tabsThermenfans,
					tabBarStyle: hidesTabBar(route) ? hiddenTabBarStyle : computedTabBarStyle,
					tabBarItemStyle: { flex: Dimens.flex11 },
					tabBarIcon: ({ color }) => <SvgIcon name={SvgIconType.starOutline} size={Dimens.iconSize20} tintColor={color} />,
				})}
			/>
			<Tab.Screen
				component={InspirationStackNavigator}
				name={Constants.screenTabInspiration}
				options={{
					headerShown: false,
					title: Translations.tabsRelaxation,
					tabBarItemStyle: { flex: Dimens.flex11 },
					tabBarIcon: ({ color }) => <SvgIcon name={SvgIconType.relaxation} size={Dimens.iconSize19} tintColor={color} />,
				}}
			/>
			<Tab.Screen
				component={BathCardStackNavigator}
				name={Constants.screenTabBathCard}
				options={{
					title: Translations.tabsBathCard,
					tabBarIcon: ({ color }) => <SvgIcon name={SvgIconType.cardAlt} size={Dimens.iconSize18} tintColor={color} />,
				}}
			/>
			{/*
			 * Still a tab, so the bar keeps showing on the account, but with no item in the bar. Hiding it
			 * takes both: `BottomTabItem` wraps the button in a `flex: 1` View that renders even when the
			 * button returns null, and the width keeps the hidden route out of the tablet label-fit sum.
			 */}
			<Tab.Screen
				component={AccountStackNavigator}
				name={Constants.screenTabAccount}
				options={{
					title: Translations.tabsAccount,
					tabBarItemStyle: { display: 'none', width: Dimens.size0 },
					tabBarButton: () => null,
				}}
			/>
		</Tab.Navigator>
	);
}
